import React from 'react';
import { Package } from 'lucide-react';

const HistoryTab = ({ lot }) => {
  console.log('HistoryTab - lot.movements:', lot.movements);
  
  // Ordenar los movimientos por timestamp, más antiguo primero
  const sortedMovements = [...lot.movements].sort((a, b) => {
    // Convertir timestamps a objetos Date para comparación
    const dateA = new Date(a.timestamp.split(' ')[0] + ' ' + a.timestamp);
    const dateB = new Date(b.timestamp.split(' ')[0] + ' ' + b.timestamp);
    return dateA - dateB; // Orden ascendente (más antiguo primero)
  });
  
  console.log('HistoryTab - sortedMovements:', sortedMovements);
  
  // Filtrar movimientos duplicados o innecesarios
  const filteredMovements = sortedMovements.filter(movement => {
    // Eliminar movimientos duplicados de "Ingreso inicial" si ya existe "Creación del lote"
    if (movement.description && 
        (movement.description.includes('Ingreso inicial') || 
         movement.description.includes('Creación del lote'))) {
      // Solo mantener uno de estos movimientos (el más antiguo)
      const hasOtherInitialMovement = sortedMovements.some(m => 
        m !== movement && 
        (m.description?.includes('Ingreso inicial') || 
         m.description?.includes('Creación del lote'))
      );
      return !hasOtherInitialMovement;
    }
    return true;
  });
  
  // Determinar si es un lote relámpago
  let isLightningLot = lot.status === 'lightning' || 
                       lot.initialQuantity === null ||
                       filteredMovements.some(m => m.type === 'reconcile_lightning') ||
                       (lot.remaining_quantity < 0 && !lot.initial_quantity);
  
  // Calcular el saldo acumulado para cada movimiento
  // Comenzamos desde 0 (o el valor apropiado) y avanzamos
  let startingBalance = 0;
  
  // Para lotes relámpago, el saldo inicial es 0 (se crearon durante una venta)
  if (isLightningLot) {
    startingBalance = 0;
  } else {
    // Para lotes normales, el saldo inicial es la cantidad inicial
    startingBalance = Number(lot.initialQuantity || 0);
  }
  
  // Verificar si hay alguna venta como primer movimiento (caso de lote relámpago)
  const firstMovementIsRemoval = filteredMovements.length > 0 && 
    (filteredMovements[0].type === 'remove' || 
     (filteredMovements[0].description && 
      filteredMovements[0].description.toLowerCase().includes('venta')));
  
  // Si el primer movimiento es una venta y no hay saldo inicial, es un lote relámpago
  if (firstMovementIsRemoval && startingBalance === 0) {
    // Forzar la detección como lote relámpago
    isLightningLot = true;
  }
  
  let runningBalance = startingBalance;
  
  const movementsWithBalance = filteredMovements.map((movement, index) => {
    // Determinar si el movimiento es de entrada, salida o especial
    let isAddition = false;
    let isRemoval = false;
    let displayQuantity = Number(movement.quantity || 0);
    let effectOnBalance = 0;
    
    // Para lotes relámpago no reconciliados, el primer movimiento (venta) debe mostrar
    // la cantidad correcta aunque el valor en la base de datos sea negativo
    if (isLightningLot && index === 0 && 
        (movement.type === 'remove' || 
         (movement.description && movement.description.toLowerCase().includes('venta')))) {
      // Asegurar que displayQuantity sea positivo para mostrar correctamente en la columna de salida
      displayQuantity = Math.abs(displayQuantity);
      isRemoval = true;
      effectOnBalance = -displayQuantity;
    } else {
      // Clasificar el tipo de movimiento y su efecto en el saldo
      switch (movement.type) {
        case 'add':
          isAddition = true;
          effectOnBalance = displayQuantity;
          break;
        case 'remove':
          isRemoval = true;
          effectOnBalance = -displayQuantity;
          break;
        case 'reconcile_lightning':
          // La reconciliación establece una cantidad inicial
          // Mostrar como un ajuste especial
          if (movement.description && movement.description.includes('cantidad inicial establecida a')) {
            const match = movement.description.match(/cantidad inicial establecida a (\d+)/);
            if (match && match[1]) {
              displayQuantity = Number(match[1]);
              isAddition = true;
              // Para lotes relámpago, la reconciliación establece la cantidad inicial
              // pero no afecta al saldo, ya que la venta ya ocurrió
              effectOnBalance = 0;
            }
          }
          break;
        case 'quality_check_start':
        case 'quality_check_end':
        case 'quality_check_approved':
          // Estos son eventos de control de calidad
          // Determinar si afectan al saldo basado en la descripción
          if (movement.description) {
            if (movement.description.toLowerCase().includes('rinde')) {
              isAddition = true;
              effectOnBalance = displayQuantity;
            } else if (movement.description.toLowerCase().includes('baja')) {
              isRemoval = true;
              effectOnBalance = -displayQuantity;
            }
          }
          break;
        default:
          // Para otros tipos, intentamos determinar si es entrada o salida
          if (movement.description) {
            if (movement.description.toLowerCase().includes('ingreso') || 
                movement.description.toLowerCase().includes('creación')) {
              isAddition = true;
              effectOnBalance = displayQuantity;
            } else if (movement.description.toLowerCase().includes('venta')) {
              isRemoval = true;
              effectOnBalance = -displayQuantity;
            }
          }
          break;
      }
    }
    
    // Actualizar el saldo acumulado
    runningBalance += effectOnBalance;
    
    return {
      ...movement,
      isAddition,
      isRemoval,
      displayQuantity,
      effectOnBalance,
      balance: runningBalance
    };
  });

  return (
    <div className="p-4 bg-white dark:bg-gray-800">
      <div className="space-y-4">
        {/* Tabla de movimientos */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b dark:border-gray-700">
                <th className="py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-400 w-1/2">Movimiento</th>
                <th className="py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-400 w-1/12"></th>
                <th className="py-2 text-right text-sm font-medium text-gray-500 dark:text-gray-400 w-1/6">Entrada</th>
                <th className="py-2 text-right text-sm font-medium text-gray-500 dark:text-gray-400 w-1/6">Salida</th>
                <th className="py-2 text-right text-sm font-medium text-gray-500 dark:text-gray-400 w-1/6">Saldo</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-gray-700">
              {/* Saldo inicial - Solo para lotes normales (no relámpago) y con cantidad inicial > 0 */}
              {!isLightningLot && Number(lot.initialQuantity || 0) > 0 && (
                <tr className="text-sm">
                  <td className="py-2 text-gray-600 dark:text-gray-300">
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500">{lot.createdDate}</span>
                      <span className="font-medium">Saldo inicial</span>
                    </div>
                  </td>
                  <td className="py-2 text-gray-600 dark:text-gray-300"></td>
                  <td className="py-2 text-right text-green-600 dark:text-green-400">{lot.initialQuantity}</td>
                  <td className="py-2 text-right text-red-600 dark:text-red-400">-</td>
                  <td className="py-2 text-right font-medium text-gray-900 dark:text-white">{startingBalance}</td>
                </tr>
              )}

              {/* Movimientos ordenados del más antiguo al más nuevo */}
              {movementsWithBalance.map((movement, index) => (
                <tr key={index} className="text-sm">
                  <td className="py-2 text-gray-600 dark:text-gray-300">
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500">{movement.timestamp}</span>
                      <span>{movement.description}</span>
                    </div>
                  </td>
                  <td className="py-2 text-gray-600 dark:text-gray-300"></td>
                  <td className="py-2 text-right text-green-600 dark:text-green-400">
                    {movement.isAddition ? movement.displayQuantity : '-'}
                  </td>
                  <td className="py-2 text-right text-red-600 dark:text-red-400">
                    {movement.isRemoval ? movement.displayQuantity : '-'}
                  </td>
                  <td className="py-2 text-right font-medium text-gray-900 dark:text-white">
                    {movement.balance}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Resumen actual */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <span className="font-medium text-gray-900 dark:text-white">Saldo actual</span>
            </div>
            <div className={`text-xl font-bold ${
              Number(lot.currentQuantity) > 0 
                ? 'text-green-600 dark:text-green-400' 
                : Number(lot.currentQuantity) < 0 
                  ? 'text-red-600 dark:text-red-400' 
                  : 'text-gray-900 dark:text-white'
            }`}>
              {lot.currentQuantity} {lot.unit}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoryTab; 